/**
 * A route that is not on this workspace's plan, or an add-on that is off,
 * says so, rather than failing at the database.
 *
 * The nav hides an entry whose module is off, and that was the whole
 * gate: every route was reachable by URL regardless. So a lapsed
 * workspace that had a Cash flow tab open, or a bookmark, or a link in a
 * colleague's email, got the page, which then made reads the rules
 * refuse and rendered as a screen of errors written for a developer.
 *
 * This is not a security control. The security control is firestore.rules
 * and it works; a client-side flag never protects anything. This is about
 * the sentence a person reads when they arrive somewhere their plan does
 * not cover, which should name the plan and the way back rather than
 * saying "Missing or insufficient permissions".
 */
import type { ReactNode } from 'react';
import { ADDONS, PLANS, formatZarCents, givesGrantsOnly, orgKindOf, type AddonKey, type ModuleKey } from '@claripick/shared';
import { useAddons, useOrg } from '@/lib/org';
import { ButtonLink, Card, PageHeader } from '@/components';

const WHAT: Record<ModuleKey, { title: string; body: string }> = {
  seeker: {
    title: `${PLANS.small.name} is not on your plan`,
    body: `Grants, your Funders register and the contract reader are ${PLANS.small.name}, at ${formatZarCents(PLANS.small.monthlyCents)} a month. Everything you have already recorded is still here and comes straight back when you subscribe.`,
  },
  grantmaker: {
    title: `${PLANS.fund.name} is not on your plan`,
    body: `Calls for applications, review, board decisions, payments to grantees and evidence are ${PLANS.fund.name}. Nothing has been removed; the screens open again when you subscribe.`,
  },
  fundraising: {
    title: 'Donors and section 18A are not on your plan',
    body: 'Donor records, donations and 18A receipting are an add-on. Your existing donor records and issued receipts are untouched.',
  },
  me: {
    title: 'Outcomes are not on your plan',
    body: 'Indicators, targets and actuals are an add-on. The indicators you have already set up are still here.',
  },
  finance: {
    title: 'Cash flow and restricted funds are not on your plan',
    body: 'Cash flow and restricted funds are an add-on. Your ledger and your fund balances are untouched and come back when it is switched on.',
  },
};

/**
 * A screen from the other side of the product, reached by an old link or a
 * bookmark. An NPO receives grants and a grant maker gives them, and neither
 * is offered the other's screens: this says which side the screen is for,
 * and where this workspace's own work is, rather than offering a plan.
 */
const OTHER_SIDE = {
  // A grant maker's workspace on one of the NPO's screens.
  receiving: {
    title: 'This screen is for an organisation that receives grants',
    meta: 'This workspace gives grants.',
    body: `Grants applied for, the Funders register and the contract reader are ${PLANS.small.name}, for an NPO's own grants and the funders behind them. The grants this workspace gives are ${PLANS.fund.name}: Calls, Grantees, Board, Payments, Evidence and the SED register.`,
  },
  // An NPO's workspace on one of the grant maker's screens.
  giving: {
    title: 'This screen is for an organisation that gives grants',
    meta: 'This workspace receives grants.',
    body: `Calls for applications, review, board decisions and payments to grantees are ${PLANS.fund.name}, for the foundations, trusts, CSI teams and donors that give grants. The grants this workspace receives, and its funders, are ${PLANS.small.name}.`,
  },
};

function OtherSide({ side }: { side: keyof typeof OTHER_SIDE }) {
  const { orgSlug } = useOrg();
  const what = OTHER_SIDE[side];
  return (
    <div className="space-y-5">
      <PageHeader title={what.title} meta={what.meta} />
      <Card>
        <p className="max-w-[62ch] text-sm text-ink">{what.body}</p>
        <p className="mt-2 text-sm text-ink-2">Nothing in this workspace has changed.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <ButtonLink to={`/o/${orgSlug}`} variant="primary" size="sm">Back to Home</ButtonLink>
        </div>
      </Card>
    </div>
  );
}

/** What stays whatever the plan, said on every one of these pages. */
function StaysFree() {
  return (
    <p className="text-sm text-ink-2">
      {PLANS.starter.name} stays free whatever else changes: your statutory register, deadlines and
      reminders, the document vault and your office-bearers.
    </p>
  );
}

/**
 * `module` may be several, and then any one of them opens the route.
 *
 * Not a nicety. A grantmaking fund is created with DEFAULT_MODULES_FUND,
 * which has no `seeker`, and a fund's own awards live in the same
 * `grants` collection as an applicant's, written by recordBoardDecision
 * with `direction: 'outgoing'`. Gating the grant routes on `seeker` alone
 * shut a fund out of its own award records, and with them PayeeCard,
 * which is the only place a grantee's bank details are captured and
 * therefore the only way a disbursement batch can ever be approved. The
 * whole payment flow was unreachable.
 */
export function RequireModule({
  module,
  children,
}: {
  module: ModuleKey | ModuleKey[];
  children: ReactNode;
}) {
  const { org, orgSlug, roleCode, loading } = useOrg();
  const wanted = Array.isArray(module) ? module : [module];
  // Do not judge before the org document has arrived, or every one of
  // these routes flashes an upgrade panel on a slow connection.
  if (loading || !org) return <>{children}</>;
  // A grant maker's own workspace never opens the NPO's screens, even once
  // the Fund plan's modules (which carry the seeker toolkit) are on. A
  // route a fund also uses (its grants) lists grantmaker among its modules.
  if (givesGrantsOnly(org) && !wanted.includes('grantmaker')) return <OtherSide side="receiving" />;
  if (wanted.some((m) => org.modules?.[m] === true)) return <>{children}</>;
  // An NPO on a grant maker's screen is not offered a plan for giving grants.
  if (wanted[0] === 'grantmaker' && orgKindOf(org) === 'seeker') return <OtherSide side="giving" />;

  const what = WHAT[wanted[0]!];
  return (
    <div className="space-y-5">
      <PageHeader title={what.title} meta={what.body} />
      <Card>
        <StaysFree />
        <div className="mt-4 flex flex-wrap gap-2">
          {/* Only an admin can actually change the plan. Anyone else is
              told who to ask instead of being sent to a page that will
              turn them away. */}
          {roleCode === 'a' ? (
            <ButtonLink to={`/o/${orgSlug}/settings`} variant="primary" size="sm">Open Workspace settings</ButtonLink>
          ) : (
            <p className="text-sm text-ink-3">Ask a workspace administrator to change the plan.</p>
          )}
          <ButtonLink to={`/o/${orgSlug}`} variant="ghost" size="sm">Back to Home</ButtonLink>
        </div>
      </Card>
    </div>
  );
}

/**
 * An add-on's route while the add-on is off: what it does and how to
 * switch it on, not a 404 and not a screen of refused reads. Opened from a
 * bookmark, an old link in an email, or a colleague's message.
 */
export function RequireAddon({ addon, children }: { addon: AddonKey; children: ReactNode }) {
  const { org, orgSlug, roleCode, loading } = useOrg();
  const addons = useAddons();
  if (loading || !org) return <>{children}</>;
  const def = ADDONS[addon];
  // The add-ons built on an NPO's grants are not a grant maker's.
  if (givesGrantsOnly(org) && def.module === 'seeker') return <OtherSide side="receiving" />;
  const status = addons.status(addon);
  if (status.on) return <>{children}</>;

  const admin = roleCode === 'a';
  const needsGrantManagement = status.why === 'module' && def.module === 'seeker' && org.modules?.seeker !== true;
  const how =
    status.why === 'off'
      ? admin
        ? 'It is off for this workspace. Switch it on in Add-ons and it joins your menu straight away.'
        : 'It is off for this workspace. A workspace administrator can switch it on in Add-ons.'
      : needsGrantManagement
        ? `It works with ${PLANS.small.name}, which is not on this workspace's plan. ${admin ? 'Subscribe in Workspace settings, then ask us to switch the add-on on.' : 'Ask a workspace administrator about the plan.'}`
        : admin
          ? 'On your plan we switch add-ons on for you. Email hello@claripick.com with your workspace address and the add-on you want, and we will set it up.'
          : 'On this plan Claripick switches add-ons on. Ask a workspace administrator to email hello@claripick.com.';
  return (
    <div className="space-y-5">
      <PageHeader title={`${def.label} is an add-on`} meta={def.does} />
      <Card>
        <p className="text-sm text-ink">{how}</p>
        <p className="mt-2 text-sm text-ink-2">
          Nothing it has recorded is deleted while it is off. Switched back on, it shows everything as you left it.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {admin && status.why === 'off' ? (
            <ButtonLink to={`/o/${orgSlug}/add-ons`} variant="primary" size="sm">Open Add-ons</ButtonLink>
          ) : admin && needsGrantManagement ? (
            <ButtonLink to={`/o/${orgSlug}/settings`} variant="primary" size="sm">Open Workspace settings</ButtonLink>
          ) : admin ? (
            <ButtonLink to={`/o/${orgSlug}/add-ons`} variant="secondary" size="sm">See every add-on</ButtonLink>
          ) : null}
          <ButtonLink to={`/o/${orgSlug}`} variant="ghost" size="sm">Back to Home</ButtonLink>
        </div>
      </Card>
    </div>
  );
}

/**
 * The accounting link (Xero, QuickBooks, Sage) is not offered for now. Its
 * code is in place (features/integrations) but nothing in the app links to
 * it, and its old address says so instead of opening it.
 */
export function AccountingNotYet() {
  const { orgSlug } = useOrg();
  return (
    <div className="space-y-5">
      <PageHeader
        title="Accounting is not available yet"
        meta="Linking Claripick to Xero, QuickBooks or Sage is not part of any plan for now. Your grants' ledgers, budgets and payments are all still recorded in Claripick itself."
      />
      <Card>
        <StaysFree />
        {/* The nightly sync (scheduledAccountingSync) still runs for a
            link made before this page closed, so a workspace that has one
            is told it is running and who can change it, rather than left
            to think it stopped. */}
        <p className="mt-2 text-sm text-ink-2">
          If your workspace was already linked, the link keeps bringing your ledger in each night. To change it
          or stop it, email hello@claripick.com.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <ButtonLink to={`/o/${orgSlug}`} variant="ghost" size="sm">Back to Home</ButtonLink>
        </div>
      </Card>
    </div>
  );
}
